import { Box, Collapse, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';

import { colors } from '../../../components/theme/theme';
import CommonStatusChip from '../../../components/ui-components/StatusChipComponent';
import { OpenInvoiceApiResponse } from '../../../services/payment-services';
import { useTranslation } from '../../../utils/translations';

export interface UpcomingPaymentMobileRow {
    contractId: React.ReactNode;
    dueDate: string;
    invoiceNumber: React.ReactNode;
    amount: React.ReactNode;
    remainingDue: React.ReactNode;
    autopayEnabled: string;
    status: React.ReactNode;
    hasAccordion?: boolean;
    originalInvoice: OpenInvoiceApiResponse;
}

interface UpcomingPaymentMobileCardProps {
    row: UpcomingPaymentMobileRow;
    renderAccordionContent: (row: Record<string, unknown>) => React.ReactNode;
}

/**
 * Mobile/tablet-only card rendering of a single upcoming-payment invoice row (collapsed
 * summary + expandable fee/payment-calculation breakdown). Desktop keeps using CommonTable
 * in upcoming-payments.tsx; both share the same renderAccordionContent so expanded details
 * stay identical between layouts.
 */
export const UpcomingPaymentMobileCard: React.FC<UpcomingPaymentMobileCardProps> = ({
    row,
    renderAccordionContent,
}) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const isAutopay = row.autopayEnabled === 'Yes';
    const canExpand = row.hasAccordion !== false;

    return (
        <Box
            sx={{
                border: `1px solid ${expanded ? colors.primaryLight[100] : colors.greyPreset[30]}`,
                borderRadius: 1.5,
                backgroundColor: colors.fullWhite,
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{ p: 1.5, cursor: canExpand ? 'pointer' : 'default' }}
                onClick={() => canExpand && setExpanded(prev => !prev)}
            >
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    {canExpand && (
                        <span
                            className="icon-arrow-drop-down"
                            style={{
                                fontSize: 20,
                                color: colors.greyPreset[100],
                                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                                display: 'inline-block',
                            }}
                        />
                    )}
                    <Typography variant="subtitle1" fontWeight={700}>
                        {row.contractId}
                    </Typography>
                    <CommonStatusChip
                        label={
                            isAutopay
                                ? t('payments.upcomingPayments.autopay') || 'Autopay'
                                : t('payments.upcomingPayments.noAutopay') || 'No Autopay'
                        }
                        chipBgColor={isAutopay ? colors.successPreset[30] : colors.errorPreset[20]}
                        chipTextColor={isAutopay ? colors.fullWhite : colors.errorPreset[10]}
                        sx={{ fontSize: 11, padding: '2px 10px' }}
                    />
                </Stack>

                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1.5}
                    flexWrap="wrap"
                    mt={1.5}
                >
                    <Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
                            {(t('payments.upcomingPayments.invoiceDueDate') || 'Invoice Due Date').toUpperCase()}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                            {row.dueDate}
                        </Typography>
                    </Stack>
                    <Box onClick={e => e.stopPropagation()}>{row.status}</Box>
                </Stack>

                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={1}
                    mt={1.5}
                    pt={1.5}
                    sx={{ borderTop: `1px solid ${colors.greyPreset[20]}` }}
                >
                    <Stack spacing={0.25}>
                        <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
                            {(t('payments.upcomingPayments.invoiceNumber') || 'Invoice #').toUpperCase()}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                            {row.invoiceNumber}
                        </Typography>
                    </Stack>
                    <Stack spacing={0.25} alignItems="flex-end">
                        <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
                            {(t('payments.upcomingPayments.amount') || 'Amount Due').toUpperCase()}
                        </Typography>
                        {row.amount}
                    </Stack>
                    <Stack spacing={0.25} alignItems="flex-end">
                        <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
                            {(t('payments.upcomingPayments.remainingDue') || 'Remaining Due').toUpperCase()}
                        </Typography>
                        {row.remainingDue}
                    </Stack>
                </Stack>
            </Box>
            {canExpand && (
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    {renderAccordionContent(row as unknown as Record<string, unknown>)}
                </Collapse>
            )}
        </Box>
    );
};
